/* eslint-disable @typescript-eslint/no-empty-function */
import useFilters, {
  ChangeFilterProps,
  FilterDefinitions,
  FilterDefinitionsKeys,
} from 'hooks/useFilters/useFilters';
import { CaseFilters, useCasesQuery } from 'queries/cases/queries';
import { CaseListItem } from 'queries/cases/types';
import { useMemo, useState, useCallback, useContext, createContext, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { LOCAL_STORAGE_ANALITICS } from 'config/constants';
import { useQueryClient } from '@tanstack/react-query';
import { hiddenNewFlag } from './helpers/cases.helper';
import { CaseActionTableItem } from './components/CaseActionsTable/CaseActionsColumns';
import useCaseModal from './hooks/useCaseModal';
import { useFeatureFlag } from '@shape/feature-flag';

export type CasesContextType = {
  queryFilters: CaseFilters;
  onChangeQueryFilters: (filters: CaseFilters) => void;
  data: CaseListItem[];
  dataToCount: (key: keyof CaseListItem) => CaseListItem[];
  filteredData: CaseListItem[];
  isFetching: boolean;
  onChangeFilters: ({
    filterName,
    filterValue,
    multivalueMode,
  }: ChangeFilterProps<CaseListItem>) => void;
  applyFilters: (
    filtersToApply: FilterDefinitions<CaseListItem>,
    data: CaseListItem[],
  ) => CaseListItem[];
  allActiveFilters: Partial<{ [key in FilterDefinitionsKeys<CaseListItem>]: string[] }>;
  selectedCase?: CaseListItem;
  changeSelectedCase: (row: CaseListItem | undefined) => void;
  resetFilters: () => void;
  setshowAnalytics: (value: boolean) => void;
  showAnalytics: boolean;
  actionsList: CaseActionTableItem[];
  enableCasesAnalytics: boolean;
};
export const CasesContext = createContext<CasesContextType>({
  queryFilters: {},
  onChangeQueryFilters: () => {},
  data: [],
  dataToCount: () => [],
  filteredData: [],
  isFetching: false,
  onChangeFilters: () => {},
  applyFilters: () => [],
  allActiveFilters: {},
  selectedCase: undefined,
  changeSelectedCase: () => {},
  resetFilters: () => {},
  setshowAnalytics: (_value: boolean) => {},
  showAnalytics: false,

  actionsList: [],
  enableCasesAnalytics: false,
});
const URL_PARAM_NAME = 'caseId';
export const casesExpandFeatureFlag = 'cases-expand';

const CasesContextProvider = ({
  defaultQueryFilters = {},
  defaultFilters = {},
  children,
}: {
  children: React.ReactNode;
  defaultQueryFilters?: CaseFilters;
  defaultFilters?: Partial<{ [key in FilterDefinitionsKeys<CaseListItem>]: string[] }>;
}) => {
  const queryClient = useQueryClient();
  const { state: navigationState } = useLocation();
  const { openCaseModal } = useCaseModal();
  const [queryFilters, setQueryFilters] = useState<CaseFilters>(defaultQueryFilters);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get(URL_PARAM_NAME);
  const { data, isLoading } = useCasesQuery({
    filters: queryFilters,
  });
  const enableCasesAnalytics = useFeatureFlag(casesExpandFeatureFlag);

  const storagedCasesExpand = localStorage.getItem(casesExpandFeatureFlag);

  const isFirstTimeSeeingAnalytics = enableCasesAnalytics && storagedCasesExpand === null;

  const localShowAnalytics = isFirstTimeSeeingAnalytics
    ? true
    : JSON.parse(storagedCasesExpand ?? 'false');
  const [showAnalytics, setshowAnalytics] = useState<boolean>(localShowAnalytics);

  const onChangeCasesExpand = useCallback((value: boolean) => {
    changeSelectedCase();
    setshowAnalytics(!value);
    localStorage.setItem(LOCAL_STORAGE_ANALITICS, String(!value));
  }, []);

  const [filteredData, setFilteredData] = useState<CaseListItem[]>(data ?? []);
  const [selectedCase, setSelectedCase] = useState<CaseListItem | undefined>(
    navigationState?.selectedItem,
  );

  const changeSelectedCase = useCallback(
    (caseItem?: CaseListItem) => {
      setSelectedCase(caseItem);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (caseItem?.id) {
          newParams.set(URL_PARAM_NAME, caseItem.id);
        } else {
          newParams.delete(URL_PARAM_NAME);
        }
        return newParams;
      });
      hiddenNewFlag({ caseItem, queryClient, queryFilters });
    },
    [setSearchParams, queryClient, queryFilters],
  );
  useEffect(() => {
    if (data && data.length > 0 && selectedId && !selectedCase) {
      const caseItem = data.find((caseItem) => caseItem.id === selectedId);
      if (showAnalytics) {
        openCaseModal(caseItem);
      }
      changeSelectedCase(caseItem);
    }
  }, [selectedId, data, changeSelectedCase, selectedCase]);
  const {
    onChange: onChangeFilters,
    allActiveFilters,
    applyFilters,
    resetFilters,
  } = useFilters<CaseListItem>({
    data,
    identical: false,
    onChangeFilters: (newFilteredData) => {
      if (!isLoading) {
        if (!(newFilteredData.length === 0 && filteredData.length === 0)) {
          setFilteredData(newFilteredData);
        }
      }
    },
    multipleFieldSearch: ['asset.id', 'name', 'event_code', 'dimension', 'description', 'id'],
    defaultFilters,
  });

  const dataToCount = useCallback(
    (key: keyof CaseListItem): CaseListItem[] => {
      const filtersKey = allActiveFilters[key] ?? [];

      if (filtersKey.length > 0) {
        const filteredWithoutThisKey = applyFilters({ ...allActiveFilters, [key]: [] }, data) ?? [];

        return filteredWithoutThisKey;
      }

      return filteredData;
    },
    [allActiveFilters, applyFilters, data, filteredData],
  );
  const actions = useMemo(() => {
    const filterKeys = Object.keys(allActiveFilters) as (keyof CaseListItem)[];
    const actionFilters = filterKeys.filter(
      (filterkey) =>
        filterkey.includes('actions.') && (allActiveFilters[filterkey] ?? [])?.length > 0,
    );
    const dataWithoutActionFilter = dataToCount(actionFilters[0]);

    return dataWithoutActionFilter.flatMap((caseItem) =>
      caseItem.actions?.map((action) => ({
        ...action,
        case_name: caseItem.name,
        owner_mail: caseItem.owner_mail,
        case_id: caseItem.id,
        external_reference_type: action.external_reference?.type,
        caseStatus: caseItem.status,
        assetId: caseItem.asset.id,
        updated_at: caseItem.updated_at,
        is_able_to_operate: caseItem.is_able_to_operate,
      })),
    );
  }, [allActiveFilters, dataToCount]);

  const context = useMemo(
    () => ({
      allActiveFilters,
      applyFilters,
      onChangeFilters,
      queryFilters,
      onChangeQueryFilters: setQueryFilters,
      data,
      dataToCount,
      filteredData,
      showAnalytics: enableCasesAnalytics && showAnalytics,
      enableCasesAnalytics,
      isFetching: isLoading,
      resetFilters,
      setshowAnalytics: onChangeCasesExpand,
      changeSelectedCase,
      selectedCase,
      actionsList: actions,
    }),
    [
      allActiveFilters,
      applyFilters,
      onChangeFilters,
      queryFilters,
      setQueryFilters,
      data,
      dataToCount,
      filteredData,
      enableCasesAnalytics,
      showAnalytics,
      isLoading,
      onChangeCasesExpand,
      resetFilters,
      changeSelectedCase,
      selectedCase,

      actions,
    ],
  );

  return (
    <CasesContext.Provider value={context} data-testid="cases-page-content-list">
      {children}
    </CasesContext.Provider>
  );
};

export default CasesContextProvider;

export const useCaseContext = () => {
  return useContext(CasesContext);
};
